import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { ComponentProps, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import * as yup from 'yup';

import { RequestError } from 'src/api/api';
import { getRandomEmail, isDevelopment } from 'src/utils';
import { useAbortableCallback } from 'src/utils/hooks/use-abortable';
import { PolicyLink } from 'src/widgets/policy';

import { useAnalytics } from '@features/Analytics';
import { fbAnalytics } from '@features/Analytics';
import {
  tikTokAdvancedMatching,
  tikTokEvent,
} from '@features/Analytics/services/tik-tok-pixel';
import { twitterEvent } from '@features/Analytics/services/twitter-pixel';

import AffirmationsIcon from '../../../../../src/assets/images/quiz/affirmation-icon-area.svg';
import HealingIcon from '../../../../../src/assets/images/quiz/healing-icon-area.svg';
import LoveIcon from '../../../../../src/assets/images/quiz/love-icon-area.svg';
import { Image } from '../../../common/Image';
import Button from '../../../common/button/button';
import FormModal from '../../../common/modal/form-modal/form-modal';
import { useStores } from '../../../common/root-store-provider/root-store-provider';
import styles from './enter-email-step.module.scss';

const FORM_SCHEMA = yup.object().shape({
  email: yup.string().trim().required().email(),
});

const FORM_INITIAL_VALUES = {
  email: '',
};

export interface Props extends ComponentProps<typeof FormModal> {}

const EnterEmail = observer(
  ({ goToNextStep }: { goToNextStep: () => void }) => {
    const { authStore, quizStore } = useStores();
    const { trackPinterestEvent, trackGoogleEvent } = useAnalytics();

    const {
      values,
      handleChange,
      handleBlur,
      handleSubmit,
      validateOnMount,
      touched,
      errors,
      isValid,
      isSubmitting,
      setFieldValue,
    } = useFormik({
      validateOnMount: true,
      initialValues: FORM_INITIAL_VALUES,
      validationSchema: FORM_SCHEMA,
      onSubmit: useAbortableCallback(
        async (abortSignal, values, { setFieldError }) => {
          trackGoogleEvent({
            eventName: 'onboarding_email_tapped',
          });
          trackPinterestEvent({ eventName: 'Signup' });

          fbAnalytics({
            eventName: 'CompleteRegistration',
            postPath: 'event_completed_registration',
            postOptions: {
              email: values.email,
              first_name: quizStore.name === '' ? undefined : quizStore.name,
              date_of_birth: dayjs(quizStore.dateOfBirth!).format('YYYY-MM-DD'),
              gender: quizStore.getGender() ?? undefined,
            },
          });

          //TODO: заменить инвент на валидный ивнет для твиттера
          twitterEvent('example1', { email_address: values.email });
          tikTokAdvancedMatching(values.email);
          tikTokEvent('CompleteRegistration');

          try {
            await authStore.register({
              email: values.email,
              firstname: quizStore.name,
              birthday: dayjs(quizStore.dateOfBirth!).format('YYYY-MM-DD'),
              abortSignal,
            });
          } catch (error) {
            if (error instanceof RequestError && error.statusCode === 409) {
              setFieldError('email', 'This email is already in use');
              return;
            }
            throw error;
          } finally {
            authStore.auth_token ? goToNextStep() : null;
          }
        },
        [authStore],
      ),
    });

    const handleRandomGenerate = useCallback(() => {
      setFieldValue('email', getRandomEmail());
    }, [setFieldValue]);

    return (
      <Form onSubmit={handleSubmit} className={styles.container}>
        <Form.Group className={styles.emailGroup}>
          <Form.Control
            type="email"
            autoComplete="email"
            placeholder="Email"
            name="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={touched.email && errors.email != null}
          />
          {isDevelopment && (
            <Button
              data-testid="random-email"
              className={styles.randomButton}
              onClick={handleRandomGenerate}
              size="sm">
              random
            </Button>
          )}
          <Form.Control.Feedback type="invalid">
            {errors.email}
          </Form.Control.Feedback>
        </Form.Group>
        <div className={styles.highlights}>
          <h4 className={styles.highlightsTitle}>
            Love compatibility, Lunar Calendar
            <br />
            and many more...
          </h4>
          <ul className={styles.highlightsList}>
            <li>
              <LoveIcon /> Relationship
            </li>
            <li>
              <AffirmationsIcon /> Affirmations
            </li>
            <li>
              <HealingIcon /> Healing
            </li>
          </ul>
        </div>
        <div className={styles.members}>
          <Image src="members.webp" width={76} height={32} alt="" /> <b>+90k</b>{' '}
          members have already joined
        </div>
        <p className={styles.privacy}>
          By submitting you agree to our <PolicyLink place="email_modal" />. We
          respect your privacy and we are committed to protecting your personal
          information.
        </p>
        <Button
          type="submit"
          disabled={!isValid}
          showLoadingIndicator={isSubmitting}
          data-testid="submit">
          Submit
        </Button>
      </Form>
    );
  },
);

export default EnterEmail;
